import i18n from "i18next";
import { initReactI18next } from "react-i18next";

const TRANSLATIONS_STORAGE_KEY = "translations";
const LOCALE_STORAGE_KEY = "locale";

export function getSavedLocale() {
  try {
    return (
      localStorage.getItem(LOCALE_STORAGE_KEY) ||
      localStorage.getItem("cdic_selected_language") ||
      "en"
    );
  } catch (error) {
    return "en";
  }
}

function readStoredTranslations() {
  try {
    const raw =
      localStorage.getItem(TRANSLATIONS_STORAGE_KEY) ||
      localStorage.getItem("cdic_app_translations");
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    return {};
  }
}

function resourcesFromPayload(data) {
  const resources = {};
  if (!data || typeof data !== "object" || Array.isArray(data)) return resources;

  Object.keys(data).forEach((locale) => {
    const bundle = data[locale]?.translation;
    if (bundle && typeof bundle === "object") {
      resources[locale] = { translation: bundle };
    }
  });

  return resources;
}

function languageNameEntries(languages) {
  if (Array.isArray(languages)) {
    return languages
      .filter((entry) => entry && (!entry.property || entry.property === "NAME"))
      .map((entry) => ({ locale: entry.locale, value: entry.value }));
  }
  if (languages && typeof languages === "object") {
    return Object.keys(languages).map((locale) => ({
      locale,
      value:
        typeof languages[locale] === "string"
          ? languages[locale]
          : languages[locale]?.value,
    }));
  }
  return [];
}

function collectSmartSetupFields(smartSetup) {
  const source =
    smartSetup?.data?.trackedentityattributes ||
    smartSetup?.data?.trackedEntityAttributes ||
    smartSetup?.data?.programstages ||
    smartSetup?.data?.programStages
      ? smartSetup.data
      : smartSetup || {};

  const attributes =
    source.trackedentityattributes || source.trackedEntityAttributes || [];
  const stages = source.programstages || source.programStages || [];
  const fields = [...attributes];

  stages.forEach((stage) => {
    const elements = stage?.dataelements || stage?.dataElements || [];
    elements.forEach((element) => fields.push(element));
  });

  return fields;
}

export function mergeSmartSetupTranslations(smartSetup) {
  const stored = readStoredTranslations();
  const englishByKey = {};

  collectSmartSetupFields(smartSetup).forEach((field) => {
    const key = field?.name;
    if (!key) return;

    const entries = languageNameEntries(field.languages);
    const englishEntry = entries.find(
      (entry) => String(entry.locale || "").toLowerCase() === "en" && entry.value
    );
    const english = englishEntry?.value || key;
    englishByKey[key] = english;

    entries.forEach((entry) => {
      if (!entry.locale) return;
      if (!stored[entry.locale]) stored[entry.locale] = { translation: {} };
      if (!stored[entry.locale].translation) stored[entry.locale].translation = {};
      stored[entry.locale].translation[key] = entry.value || english;
    });
  });

  Object.keys(englishByKey).forEach((key) => {
    if (!stored.en) stored.en = { translation: {} };
    if (!stored.en.translation) stored.en.translation = {};
    if (!stored.en.translation[key]) stored.en.translation[key] = englishByKey[key];

    Object.keys(stored).forEach((locale) => {
      if (!stored[locale].translation) stored[locale].translation = {};
      if (!stored[locale].translation[key]) {
        stored[locale].translation[key] = englishByKey[key];
      }
    });
  });

  applyTranslations(stored);
}

export function applyTranslations(data) {
  const resources = resourcesFromPayload(data);
  const locales = Object.keys(resources);
  if (!locales.length) return;

  try {
    const serialized = JSON.stringify(data);
    localStorage.setItem(TRANSLATIONS_STORAGE_KEY, serialized);
    localStorage.setItem("cdic_app_translations", serialized);
  } catch (error) {
    console.error("Failed to store translations", error);
  }

  locales.forEach((locale) => {
    i18n.addResourceBundle(
      locale,
      "translation",
      resources[locale].translation,
      true,
      true
    );
  });

  const savedLocale = getSavedLocale();
  if (i18n.language !== savedLocale) {
    i18n.changeLanguage(savedLocale);
  }
}

i18n.use(initReactI18next).init({
  lng: getSavedLocale(),
  fallbackLng: "en",
  resources: resourcesFromPayload(readStoredTranslations()),
  keySeparator: false,
  nsSeparator: false,
  interpolation: {
    escapeValue: false,
  },
});

i18n.on("languageChanged", (lng) => {
  if (!lng) return;
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, lng);
  } catch (error) {
    console.error("Failed to store selected language", error);
  }
});

export default i18n;
